import { createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { createRoute } from "@tanstack/solid-router";
import { ArchiveRestore, Trash2 } from "@fleet/icons";

import { Card, CardContent, PageContainer, PageHeader } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { ChecklistIllustration } from "@/components/ui/illustrations";
import { appRoute } from "./route";
import { useI18n } from "@/providers/i18n-provider";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { createPowerSyncQuery } from "@habits/shared/solid";
import { getTrashedHabitsQuery, type HabitRow, queryKeys, TRASH_PURGE_DAYS } from "@habits/shared";
import { useHabitMutations } from "@/features/habits/hooks/use-habit-mutations";
import { DEFAULT_HABIT_COLOR, DEFAULT_HABIT_ICON } from "@/features/habits/constants";

/**
 * Trash (UNDO-TRASH-LIGHT L2): soft-deleted habits (`deleted_at`).
 *
 * - Deleting from the UI = moving them here; CHECK-INS and reminders are
 *   PRESERVED (design decision: restoring returns the habit with all its
 *   streaks/statistics intact, as if it had never left).
 * - "Restore" clears `deleted_at` (undoable: the pill offers re-delete).
 * - "Delete forever" performs the real DELETE (server cascade) after
 *   explicit confirmation — this is irreversible and does NOT record an undo.
 * - Normal views (Today/Habits/calendar/stats/streaks) exclude deleted items
 *   through `getHabitsQuery` (deleted_at IS NULL): zero ad-hoc filters.
 */
function TrashPage() {
  const { t, locale } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const mutations = useHabitMutations();

  const [confirmPurge, setConfirmPurge] = createSignal<HabitRow | null>(null);

  const trashed = createPowerSyncQuery<HabitRow>({
    queryKey: () => queryKeys.habits.trashed(session.userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const uid = session.userId();
      return dbInstance && uid ? getTrashedHabitsQuery(dbInstance, uid) : null;
    },
    enabled: () => Boolean(drizzle()) && Boolean(session.userId()),
    suspense: false,
  });

  const MS_PER_DAY = 86_400_000;

  /** “Deleted X ago” — relative time from `deleted_at` (trash checklist). */
  const deletedAgoLabel = (iso: string | null) => {
    if (!iso) {
      return "";
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
      return "";
    }
    const rtf = new Intl.RelativeTimeFormat(locale(), { numeric: "auto" });
    const diffDays = Math.round((date.getTime() - Date.now()) / MS_PER_DAY);
    if (diffDays === 0) {
      const diffHours = Math.round((date.getTime() - Date.now()) / 3_600_000);
      if (diffHours === 0) {
        return rtf.format(Math.round((date.getTime() - Date.now()) / 60_000), "minute");
      }
      return rtf.format(diffHours, "hour");
    }
    return rtf.format(diffDays, "day");
  };

  /**
   * “will be permanently deleted in Y days” — countdown = deleted_at +
   * TRASH_PURGE_DAYS − now. Returns the remaining days (minimum 0), or null if
   * there is no valid date.
   */
  const daysUntilPurge = (iso: string | null): number | null => {
    if (!iso) {
      return null;
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    const purgeAt = date.getTime() + TRASH_PURGE_DAYS * MS_PER_DAY;
    return Math.max(0, Math.ceil((purgeAt - Date.now()) / MS_PER_DAY));
  };

  return (
    <PageContainer maxWidth="2xl" class="space-y-6" containerClass="scrollbar-thin">
      <div data-testid="trash-view">
        <PageHeader
          title={t(msg`Trash`)}
          description={t(msg`Deleted habits keep their whole history and can be restored anytime.`)}
          backTo="/profile"
          icon={Trash2}
        />
      </div>

      <Show
        when={(trashed.data ?? []).length > 0}
        fallback={
          <div data-testid="trash-empty">
            <EmptyState
              icon={Trash2}
              illustration={ChecklistIllustration}
              title={t(msg`Trash is empty`)}
              description={t(msg`Habits you delete land here — nothing is lost right away.`)}
            />
          </div>
        }
      >
        <div class="flex flex-col gap-2">
          <For each={trashed.data ?? []}>
            {(habit) => (
              <Card data-testid="trash-row" data-habit-name={habit.name ?? ""}>
                <CardContent class="flex items-center gap-3 p-3">
                  <span
                    class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-lg opacity-70"
                    style={{ "background-color": `${habit.color ?? DEFAULT_HABIT_COLOR}26` }}
                  >
                    {habit.icon ?? DEFAULT_HABIT_ICON}
                  </span>
                  <div class="min-w-0 flex-1">
                    <p class="truncate text-sm font-medium">{habit.name}</p>
                    <p class="mt-0.5 text-xs text-muted-foreground">
                      {t(msg`Deleted {ago}`, { ago: deletedAgoLabel(habit.deleted_at) })}
                    </p>
                    <Show when={daysUntilPurge(habit.deleted_at) !== null}>
                      <p
                        class="text-xs text-muted-foreground/80"
                        data-testid="trash-purge-countdown"
                      >
                        {t(msg`Will be permanently deleted in {days} days`, {
                          days: daysUntilPurge(habit.deleted_at) ?? 0,
                        })}
                      </p>
                    </Show>
                  </div>
                  <div class="flex shrink-0 items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="trash-restore"
                      disabled={!drizzle() || mutations.isSubmitting()}
                      onClick={() => void mutations.restoreHabit(habit.id)}
                    >
                      <ArchiveRestore class="mr-1 h-4 w-4" />
                      {t(msg`Restore`)}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      class="text-destructive hover:text-destructive"
                      data-testid="trash-delete-forever"
                      disabled={!drizzle() || mutations.isSubmitting()}
                      onClick={() => setConfirmPurge(habit)}
                    >
                      <Trash2 class="mr-1 h-4 w-4" />
                      {t(msg`Delete forever`)}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </For>
        </div>
      </Show>

      <ConfirmDialog
        open={confirmPurge() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmPurge(null);
          }
        }}
        title={t(msg`Delete forever?`)}
        description={t(
          msg`This permanently deletes the habit and its whole history. This cannot be undone.`,
        )}
        confirmLabel={t(msg`Delete forever`)}
        variant="destructive"
        onConfirm={() => {
          const habit = confirmPurge();
          setConfirmPurge(null);
          if (habit) {
            void mutations.deleteHabitForever(habit.id);
          }
        }}
      />
    </PageContainer>
  );
}

export const profileTrashRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/profile/trash",
  component: TrashPage,
});
